import { HTMLAttributes, ReactNode, useRef } from "react";
import useGetBuilderPhoneDimensions, {
  PIXELS_TO_SUBTRACT_FROM_FULL_HEIGHT,
} from "@src/components/libraryItemDetailPages/hooks/useGetBuilderPhoneDimensions";
import ScrollableFlexGrow from "@src/components/ui/flexBox/ScrollableFlexGrow";
import { Skeleton } from "@src/ui/skeleton";
import PhoneOutline from "@src/components/libraryItemDetailPages/shared/PhoneOutline";
import useGetRefPositionAndDimensions from "@src/components/libraryItemDetailPages/hooks/useGetRefPositionAndDimensions";
import Text from "@ui/text";
import { Card } from "@src/ui/card";
import { cn } from "@src/ui/lib/utils";

export const DETAIL_CARD_MIN_WIDTH = 360;
export const EMPTY_SCREENS_ARRAY = [...Array(10).keys()];

type ChildrenProp = {
  children: ReactNode;
  includePadding?: boolean;
};
type ChildrenAndDivRest = ChildrenProp & HTMLAttributes<HTMLDivElement>;
function LibraryItemDetailPage({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest): JSX.Element {
  return (
    <div
      className={cn("flex flex-1 flex-col self-stretch", className)}
      {...restProps}
    >
      {children}
    </div>
  );
}

LibraryItemDetailPage.Header = function Header({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div
      className={cn(
        "flex h-[60px] items-center justify-between self-stretch px-3",
        className,
      )}
      {...restProps}
    >
      {children}
    </div>
  );
};

LibraryItemDetailPage.Content = function Content({
  children,
  className,
  includePadding = true,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div
      className={cn(
        "flex flex-1 self-stretch gap-3",
        includePadding ? "px-3" : undefined,
        className,
      )}
      {...restProps}
    >
      {children}
    </div>
  );
};

LibraryItemDetailPage.LeftPane = function LeftPane({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <Card
      variant="elevated"
      className={cn(
        "relative flex w-[294px] flex-1 self-stretch overflow-hidden",
        className,
      )}
      {...restProps}
    >
      {children}
    </Card>
  );
};

LibraryItemDetailPage.CenterPane = function CenterPane({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <Card
      variant="dotted-grid"
      className={cn("flex flex-1 flex-col self-stretch", className)}
      {...restProps}
    >
      {children}
    </Card>
  );
};

LibraryItemDetailPage.RightPane = function RightPane({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <Card
      variant="elevated"
      className={cn(
        "relative flex w-[294px] self-stretch overflow-hidden",
        className,
      )}
      {...restProps}
    >
      {children}
    </Card>
  );
};

LibraryItemDetailPage.RightPaneLoading = function RightPaneLoading({
  className,
  ...restProps
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <LibraryItemDetailPage.RightPane className={className} {...restProps}>
      <div className="flex flex-1 flex-col gap-[12px] self-stretch p-[16px]">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-4 h-12 self-stretch" />
        <Skeleton className="h-12 self-stretch" />
        <Skeleton className="h-12 self-stretch" />
      </div>
    </LibraryItemDetailPage.RightPane>
  );
};

LibraryItemDetailPage.ManageContent = function ManageContent({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div
      className={cn(
        "mx-3 flex flex-1 self-stretch rounded-2xl px-3",
        className,
      )}
      {...restProps}
    >
      {children}
    </div>
  );
};

LibraryItemDetailPage.ManageLeftPane = function ManageLeftPane({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div
      className={cn(
        "hide-scrollbar flex w-[240px] flex-col self-stretch rounded-2xl px-6",
        className,
      )}
      {...restProps}
    >
      {children}
    </div>
  );
};

LibraryItemDetailPage.ManageCenterPane = function ManageCenterPane({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div className={cn("flex flex-1 self-stretch", className)} {...restProps}>
      {children}
    </div>
  );
};

LibraryItemDetailPage.ManageContainer = function ManageContainer({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div
      className={cn("mb-16 flex flex-col gap-4 self-stretch", className)}
      {...restProps}
    >
      {children}
    </div>
  );
};

LibraryItemDetailPage.ManageTable = function ManageTable({
  children,
  className,
  ...restProps
}: ChildrenAndDivRest) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col self-stretch rounded-xl border border-gray-alpha-5 p-4",
        className,
      )}
      {...restProps}
    >
      {children}
    </div>
  );
};

LibraryItemDetailPage.CenterPaneLoading = function CenterPaneLoading({
  screenType,
}: {
  screenType: "grid" | "fullscreen";
}) {
  return (
    <LibraryItemDetailPage.CenterPane>
      {screenType === "grid" && (
        <>
          <div className="absolute inset-x-0 top-0 flex h-[48px] items-center gap-[16px] self-stretch p-[16px]">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="size-2 rounded-full" />
            <Skeleton className="h-4 w-12" />
          </div>
          <LibraryItemDetailPage.CenterPaneGridLoading />
        </>
      )}
      {screenType === "fullscreen" && (
        <LibraryItemDetailPage.CenterPaneFullScreenLoading />
      )}
    </LibraryItemDetailPage.CenterPane>
  );
};

LibraryItemDetailPage.CenterPaneGridLoading = function CenterPaneGridLoading() {
  const ref = useRef(null);
  const phoneDimensions = useGetBuilderPhoneDimensions({ ref });
  return (
    <>
      <ScrollableFlexGrow
        flex={1}
        alignSelf="stretch"
        style={{ overflow: "hidden" }}
        innerStyle={{
          overflow: "visible",
          alignItems: "center",
          flexDirection: "row",
        }}
      >
        <div
          ref={ref}
          className={`flex flex-1 flex-wrap items-start justify-start gap-[8px] self-stretch p-[16px] pt-[48px]`}
        >
          {EMPTY_SCREENS_ARRAY.map((s, i) => (
            <PhoneOutline
              key={i}
              isSelected={false}
              parentVerticalPadding={10}
              parentDimensions={phoneDimensions}
              content={<Skeleton className="flex-1 self-stretch" />}
              style={{ backgroundColor: "transparent", boxShadow: "none" }}
            />
          ))}
        </div>
      </ScrollableFlexGrow>
    </>
  );
};

LibraryItemDetailPage.CenterPaneFullScreenLoading =
  function CenterPaneFullScreenLoading() {
    const ref = useRef<HTMLDivElement>(null);
    const phoneDimensions = useGetRefPositionAndDimensions({
      ref,
      sizeOffset: PIXELS_TO_SUBTRACT_FROM_FULL_HEIGHT,
    });
    return (
      <div
        ref={ref}
        className="flex flex-1 flex-wrap items-center justify-center gap-[8px] self-stretch p-[16px]"
      >
        <PhoneOutline
          isSelected={false}
          parentVerticalPadding={0}
          parentDimensions={phoneDimensions}
          content={<Skeleton className="flex-1 self-stretch" />}
          style={{ backgroundColor: "transparent", boxShadow: "none" }}
        />
      </div>
    );
  };

type LeftNavTitleProps = {
  title: string;
};
LibraryItemDetailPage.LeftNavTitle = function LeftNavTitle(
  props: LeftNavTitleProps,
) {
  return (
    <div className="flex pb-[24px] pl-[16px] pt-[36px]">
      <Text type="H3" fontWeight="SemiBold">
        {props.title}
      </Text>
    </div>
  );
};

export default LibraryItemDetailPage;
